/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useState } from "react";
import { FieldCode, FieldGroup, IFieldCodeProps, useEventCallback } from "@adapptio/lib-editor-ui";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { useFloatingPanel } from "../../../ui/hooks/useFloatingPanel";
import { FloatingPanelExpressionEditorModel } from "../../../ui/components/FloatingPanelExpressionEditor/FloatingPanelExpressionEditorModel";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { IInspectorControl_Code, TInspectorControlProps } from "../InspectorControlTypes";
import { isModelValueRequired } from "@adapptio/lib-blueprint";
import { useReferenceSelectorContext } from "../../../ui/contexts/ReferenceSelectorContext";

export interface IInspectorControlCodeProps extends TInspectorControlProps<IInspectorControl_Code> {
	label?: string;
}

export const InspectorControlCode: React.FC<IInspectorControlCodeProps> = ({
	model: _model,
	label,
	language,
	options
}) => {
	const { model, validationErrors } = useBlueprintModel(_model, "constString");
	const [ expLastValue, setExpLastValue ] = useState<{ result: unknown }>(
		model?.expLastValue as { result: unknown }
	);
	const inspectorContext = useEditorInspectorContext();
	const referenceSelectorCtx = useReferenceSelectorContext();
	const path = [];

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	const handleOuterReferenceClick = useEventCallback<IFieldCodeProps["onOuterReferenceClick"]>(
		async (editorInstance) => {
			try {
				const selectedRef = await referenceSelectorCtx.getSelectedReference();

				if (selectedRef?.value) {
					handleChange(selectedRef.value);
					editorInstance.focus();
				}
			} catch (e) {
				// nvm
			}
		}
	);

	const { togglePanel, opened } = useFloatingPanel(
		FloatingPanelExpressionEditorModel,
		`panel-${model?.nodeId}`,
		{
			model: _model,
			path,
			readOnly: inspectorContext?.readOnly,
			debounceTimeout: inspectorContext?.inputDebounceTimeout,
			title: label,
			language,
			defaultRect: { width: 500, height: 400 },
			showStringValue: !model.schema.opts.allowInterpolation || !model.isExpression,
			onOuterReferenceClick: handleOuterReferenceClick
		}
	);

	useEffect(() => {
		if (model && model.schema.opts.allowInterpolation) {
			const handleLastValueChange = () => setExpLastValue(model.expLastValue as { result: unknown });

			onEvent(model.expLastValueChangeEvent, handleLastValueChange);

			return () => {
				offEvent(model.expLastValueChangeEvent, handleLastValueChange);
			};
		}
	}, [ model ]);

	const required = isModelValueRequired(model);

	if (model.schema.opts.allowInterpolation) {
		return (
			<FieldGroup grow>
				<FieldCode
					key={model.nodeId}
					label={label}
					value={model.value}
					onChange={handleChange}
					validationErrors={validationErrors}
					parseErrors={model.expParseErrors}
					resultData={model.isExpression ? expLastValue?.result : model.value}
					grow
					language={language}
					multiLine={options?.multiLine}
					height={options?.height}
					readOnly={inspectorContext?.readOnly}
					debounceTimeout={inspectorContext?.inputDebounceTimeout}
					canExpand
					onExpand={togglePanel}
					isExpanded={opened}
					clearable={!required}
					onOuterReferenceClick={handleOuterReferenceClick}
					
				/>
			</FieldGroup>
		);
	}

	return (
		<FieldGroup grow>
			<FieldCode
				key={model.nodeId}
				label={label}
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow
				language={language}
				multiLine={options?.multiLine}
				height={options?.height}
				readOnly={inspectorContext?.readOnly}
				debounceTimeout={inspectorContext?.inputDebounceTimeout}
				canExpand
				onExpand={togglePanel}
				isExpanded={opened}
				clearable={!required}
				onOuterReferenceClick={handleOuterReferenceClick}
			/>
		</FieldGroup>
	);
};

InspectorControlCode.defaultProps = {
	label: ""
};
